import React, { useState } from "react";
import { TimelineBatch } from "../../types/mes";
import {
  buildGanttSwimLanes,
  GanttBatchGroupBy,
} from "../../domain/MES/Planning/planningUtils";
import { clockTimeToMinutes } from "../../utils/processParameterSerialize";
import { dateLocale, t } from "../../i18n/locale";

interface GanttComparisonChartProps {
  plannedBatches: TimelineBatch[];
  actualBatches: TimelineBatch[];
  selectedDate: string;
  showPlanned: boolean;
  showActual: boolean;
  groupBy?: GanttBatchGroupBy;
  onGroupByChange?: (groupBy: GanttBatchGroupBy) => void;
}

const phaseColors = {
  mixing: "#4b9987",
  fermentation: "#5b8dd9",
  processing: "#e8c547",
  finalProof: "#9b59b6",
  baking: "#e67e22",
};

const GanttComparisonChart: React.FC<GanttComparisonChartProps> = ({
  plannedBatches,
  actualBatches,
  selectedDate,
  showPlanned,
  showActual,
  groupBy: controlledGroupBy,
  onGroupByChange,
}) => {
  const [internalGroupBy, setInternalGroupBy] = useState<GanttBatchGroupBy>("mixer");
  const groupBy = controlledGroupBy ?? internalGroupBy;
  const setGroupBy = onGroupByChange ?? setInternalGroupBy;

  const actualById = new Map(actualBatches.map((b) => [b.MixCode, b]));
  const laneSource = showPlanned ? plannedBatches : actualBatches;
  const swimLanes = buildGanttSwimLanes(laneSource, groupBy);

  const timeToMinutes = clockTimeToMinutes;

  const minutesToTime = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };

  const collectTimes = (batches: TimelineBatch[]) => {
    const times: number[] = [];
    batches.forEach((b) => {
      [
        b.MixingStart,
        b.MixingEnd,
        b.FermentationStart,
        b.FermentationEnd,
        b.ProcessingStart,
        b.ProcessingEnd,
        b.FinalProofStart,
        b.FinalProofEnd,
        b.BakingStart,
        b.BakingEnd,
      ].forEach((t) => {
        const m = timeToMinutes(t);
        if (m !== null) times.push(m);
      });
    });
    return times;
  };

  let minTime = 24 * 60;
  let maxTime = 0;
  const allTimes = [
    ...(showPlanned ? collectTimes(plannedBatches) : []),
    ...(showActual ? collectTimes(actualBatches) : []),
  ];
  allTimes.forEach((t) => {
    minTime = Math.min(minTime, t);
    maxTime = Math.max(maxTime, t);
  });

  if (minTime >= maxTime) {
    minTime = 4 * 60;
    maxTime = 22 * 60;
  } else {
    minTime = Math.max(0, minTime - 30);
    maxTime = Math.min(24 * 60, maxTime + 30);
  }

  const timeRange = maxTime - minTime;
  const chartWidth = 1200;
  const laneHeight = 50;
  const dualTrack = showPlanned && showActual;
  const batchHeight = dualTrack ? 52 : 40;
  const barHeight = dualTrack ? 14 : 20;
  const labelWidth = 175;
  const headerHeight = 40;

  const hourMarkers: number[] = [];
  for (let h = Math.floor(minTime / 60); h <= Math.ceil(maxTime / 60); h++) {
    hourMarkers.push(h * 60);
  }

  const getBarX = (minutes: number) =>
    labelWidth + ((minutes - minTime) / timeRange) * (chartWidth - labelWidth);
  const getBarWidth = (start: number, end: number) =>
    ((end - start) / timeRange) * (chartWidth - labelWidth);

  const totalHeight =
    headerHeight +
    swimLanes.reduce((sum, lane) => sum + lane.batches.length * batchHeight + laneHeight, 0);

  const getPhases = (batch: TimelineBatch) => [
    { name: "mixing", start: timeToMinutes(batch.MixingStart), end: timeToMinutes(batch.MixingEnd), color: phaseColors.mixing, label: "Mix" },
    { name: "fermentation", start: timeToMinutes(batch.FermentationStart), end: timeToMinutes(batch.FermentationEnd), color: phaseColors.fermentation, label: "Ferm" },
    { name: "processing", start: timeToMinutes(batch.ProcessingStart), end: timeToMinutes(batch.ProcessingEnd), color: phaseColors.processing, label: "Obr" },
    { name: "finalProof", start: timeToMinutes(batch.FinalProofStart), end: timeToMinutes(batch.FinalProofEnd), color: phaseColors.finalProof, label: "Zav" },
    { name: "baking", start: timeToMinutes(batch.BakingStart), end: timeToMinutes(batch.BakingEnd), color: phaseColors.baking, label: "Peč" },
  ];

  const renderTrack = (
    batch: TimelineBatch,
    batchY: number,
    yOffset: number,
    variant: "planned" | "actual"
  ) => {
    const opacity = variant === "planned" ? 0.9 : 0.75;
    const stroke = variant === "actual" ? "#fff" : "none";
    const strokeWidth = variant === "actual" ? 1.5 : 0;

    return getPhases(batch).map((phase) => {
      if (phase.start === null || phase.end === null) return null;
      const barX = getBarX(phase.start);
      const barW = Math.max(getBarWidth(phase.start, phase.end), 2);
      return (
        <g key={`${variant}-${phase.name}`}>
          <rect
            x={barX}
            y={batchY + yOffset}
            width={barW}
            height={barHeight}
            fill={phase.color}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={variant === "actual" ? "4,2" : undefined}
            rx={3}
            opacity={opacity}
          >
            <title>
              {variant === "planned" ? t("Plan") : t("Stvarno")} — {t(phase.label)}:{" "}
              {minutesToTime(phase.start)} - {minutesToTime(phase.end)}
            </title>
          </rect>
        </g>
      );
    });
  };

  return (
    <div className="gantt-comparison-chart">
      <div className="gantt-comparison-header">
        <h3>{t("Plan vs. stvarno — {date}", { date: new Date(selectedDate).toLocaleDateString(dateLocale()) })}</h3>
        <div className="view-toggle gantt-group-toggle">
          <button type="button" className={groupBy === "mixer" ? "active" : ""} onClick={() => setGroupBy("mixer")}>
            {t("Miješanje")}
          </button>
          <button type="button" className={groupBy === "oven" ? "active" : ""} onClick={() => setGroupBy("oven")}>
            {t("Pečenje")}
          </button>
        </div>
        <div className="gantt-comparison-legend">
          {showPlanned && (
            <span className="legend-item">
              <span className="legend-swatch planned" /> {t("Planirano")}
            </span>
          )}
          {showActual && (
            <span className="legend-item">
              <span className="legend-swatch actual" /> {t("Stvarno")}
            </span>
          )}
        </div>
      </div>

      <div className="gantt-scroll-container">
        <svg width={chartWidth} height={totalHeight} className="gantt-svg">
          <g className="gantt-time-axis">
            <rect x={0} y={0} width={chartWidth} height={headerHeight} fill="#1a1d29" />
            {hourMarkers.map((h) => {
              const x = getBarX(h);
              return (
                <g key={h}>
                  <line x1={x} y1={headerHeight} x2={x} y2={totalHeight} stroke="rgba(255,255,255,0.08)" strokeDasharray="3,3" />
                  <text x={x} y={25} textAnchor="middle" fill="#adb3bd" fontSize="13">
                    {minutesToTime(h)}
                  </text>
                </g>
              );
            })}
          </g>

          {swimLanes.map((lane, laneIdx) => {
            const laneY =
              headerHeight +
              swimLanes.slice(0, laneIdx).reduce((s, l) => s + l.batches.length * batchHeight + laneHeight, 0);

            return (
              <g key={`${groupBy}-${lane.deviceName}`}>
                <rect x={0} y={laneY} width={chartWidth} height={laneHeight} fill="#252935" />
                <text x={15} y={laneY + laneHeight / 2 + 5} fill="#e8e6e3" fontSize="15" fontWeight="600">
                  {lane.deviceName}
                </text>

                {lane.batches.map((batch, batchIdx) => {
                  const batchY = laneY + laneHeight + batchIdx * batchHeight;
                  const actual = actualById.get(batch.MixCode);
                  const plannedRow = showPlanned ? batch : actual || batch;

                  return (
                    <g key={batch.MixCode}>
                      <rect x={0} y={batchY} width={labelWidth} height={batchHeight} fill="#1e222e" />
                      <text x={15} y={batchY + batchHeight / 2 + 4} fill="#e8e6e3" fontSize="11">
                        <title>{batch.MixCode}</title>
                        {batch.MixCode.substring(0, 22)}
                      </text>
                      {dualTrack && (
                        <>
                          <text x={labelWidth - 8} y={batchY + 12} textAnchor="end" fill="#7f8c9a" fontSize="8">
                            P
                          </text>
                          <text x={labelWidth - 8} y={batchY + 32} textAnchor="end" fill="#7f8c9a" fontSize="8">
                            S
                          </text>
                        </>
                      )}
                      {showPlanned && renderTrack(plannedRow as TimelineBatch, batchY, dualTrack ? 2 : (batchHeight - barHeight) / 2, "planned")}
                      {showActual && actual && renderTrack(actual, batchY, dualTrack ? 22 : (batchHeight - barHeight) / 2, "actual")}
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>

      <style>{`
        .gantt-comparison-chart {
          background: #1a1d29;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }
        .gantt-comparison-header {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .gantt-comparison-header h3 { color: #e8e6e3; margin: 0; }
        .gantt-comparison-legend {
          display: flex;
          gap: 1rem;
          margin-left: auto;
        }
        .gantt-comparison-legend .legend-item {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.85rem;
          color: #adb3bd;
        }
        .legend-swatch {
          width: 20px;
          height: 12px;
          border-radius: 2px;
          display: inline-block;
        }
        .legend-swatch.planned { background: #4b9987; }
        .legend-swatch.actual {
          background: #4b9987;
          opacity: 0.6;
          border: 1.5px dashed #fff;
        }
      `}</style>
    </div>
  );
};

export default GanttComparisonChart;
